import { BusyDots } from '@order-up/common';

import type { ScheduledCartNotice } from '../lib/scheduledCartNotice';

interface ScheduledCartNoticePanelProps {
  readonly notice: ScheduledCartNotice;
  /** Accept the server's answer: the cart takes the new prices and drops what cannot be ordered now. */
  readonly onConfirm: () => void;
  readonly isApplying: boolean;
  /** The restaurant's storefront, for a guest who would rather change the order there. */
  readonly menuUrl: string;
}

/**
 * The server refused the cart because a price moved with the clock (Happy Hour
 * ended) or an item cannot be ordered right now. Nothing was charged. The guest
 * sees every line — new price and old — and accepts the server's answer here in
 * one step; the order total is then re-quoted before they pay.
 */
export default function ScheduledCartNoticePanel({ notice, onConfirm, isApplying, menuUrl }: ScheduledCartNoticePanelProps) {
  return (
    <div
      role="alert"
      data-testid="scheduled-cart-notice"
      className="mb-4 p-4 bg-amber-50 border-2 border-amber-300 rounded-xl"
    >
      <p className="text-sm font-bold text-amber-900 mb-1">{notice.title}</p>
      <ul className="text-sm text-amber-900 mb-3 space-y-1">
        {notice.lines.map((line) => (
          <li key={line.key} className="flex flex-col">
            <span className="font-medium">{line.text}</span>
            {line.detail && <span className="text-amber-800">{line.detail}</span>}
          </li>
        ))}
      </ul>
      <p className="text-sm text-amber-900 mb-3">Your card has not been charged.</p>
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={onConfirm}
          disabled={isApplying || notice.refusals.length === 0}
          aria-label={isApplying ? 'Updating your cart' : undefined}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-amber-600 text-white text-sm font-semibold hover:bg-amber-700 transition-colors disabled:opacity-60"
        >
          {isApplying ? <BusyDots className="h-4 w-4" /> : null}
          Update my cart
        </button>
        <a
          href={menuUrl}
          className="px-4 py-2 rounded-lg border border-amber-600 text-amber-900 text-sm font-semibold hover:bg-amber-100 transition-colors"
        >
          Back to menu
        </a>
      </div>
    </div>
  );
}
